import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styles from './exportQueue.module.scss';
import { actions } from '../../../../state';
import { Icon } from '../../../../components';
import {
  ACTIVE_EXPORT_STATUSES,
  isActiveExport,
} from '../../../../state/download/download.helpers';

const STATUS_LABELS = {
  queued: 'Laukiama',
  running: 'Eksportuojama',
  completed: 'Atlikta',
  canceled: 'Atšaukta',
  failed: 'Klaida',
};

const getExportTimestamp = exportItem =>
  Number(
    exportItem.created_at ||
      exportItem.started_at ||
      exportItem.finished_at ||
      0
  );

const formatFileType = filename => {
  const extension = filename
    ?.split('.')
    .pop()
    ?.toLowerCase();

  switch (extension) {
    case 'xlsx':
    case 'xls':
      return 'Excel';
    case 'csv':
      return 'CSV';
    case 'pdf':
      return 'PDF';
    default:
      return extension ? extension.toUpperCase() : 'Failas';
  }
};

const formatFileName = filename => {
  if (!filename) return 'Eksporto failas';

  return filename;
};

const formatExportDateTime = exportItem => {
  const timestamp = getExportTimestamp(exportItem);

  if (!timestamp) return null;

  const exportDate = new Date(timestamp);

  if (Number.isNaN(exportDate.getTime())) return null;

  const time = new Intl.DateTimeFormat('lt-LT', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(exportDate);
  const date = new Intl.DateTimeFormat('lt-LT', {
    day: '2-digit',
    month: 'short',
  }).format(exportDate);

  return `${time} ${date}`;
};

const formatFileSize = exportItem => {
  if (exportItem.file_size) {
    return exportItem.file_size;
  }

  const bytes = Number(exportItem.file_size_bytes);

  if (!bytes) return null;

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), 3);
  const value = bytes / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${
    units[unitIndex]
  }`;
};

const formatRows = value => Number(value || 0).toLocaleString('lt-LT');

const getProgressPercent = exportItem => {
  if (!exportItem.total_rows || exportItem.total_rows <= 0) return 0;

  return Math.min(
    100,
    Math.round(
      (Number(exportItem.rows_exported || 0) / exportItem.total_rows) * 100
    )
  );
};

const getDownloadFilename = exportItem =>
  exportItem.filename || `export-${exportItem.id}.xlsx`;

const ExportQueue = ({
  dispatch,
  exports,
  dismissedExportIds,
  exportQueueOpen,
  exportsLoading,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!exportQueueOpen) return undefined;

    const handleClickOutside = ({ target }) => {
      if (containerRef.current && !containerRef.current.contains(target)) {
        dispatch(actions.download.setExportQueueOpenAction(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dispatch, exportQueueOpen]);

  const visibleExports = useMemo(
    () =>
      exports
        .filter(item => !dismissedExportIds.includes(item.id))
        .sort(
          (left, right) => getExportTimestamp(right) - getExportTimestamp(left)
        ),
    [dismissedExportIds, exports]
  );

  const activeExportsCount = useMemo(
    () => visibleExports.filter(isActiveExport).length,
    [visibleExports]
  );

  const hasCompletedExports = visibleExports.some(
    exportItem => exportItem.status === 'completed'
  );

  const handleToggle = () => {
    dispatch(actions.download.setExportQueueOpenAction(!exportQueueOpen));
  };

  const handleDownload = exportItem => {
    dispatch(
      actions.download.fetchExportedFile(
        getDownloadFilename(exportItem),
        exportItem.file_url
      )
    );
  };

  const handleCancel = exportId => {
    dispatch(actions.download.cancelExportAction(exportId));
  };

  const handleClearCompleted = () => {
    dispatch(actions.download.clearCompletedExportsAction());
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={`${styles.trigger} ${
          exportQueueOpen ? styles.triggerOpen : ''
        }`}
        onClick={handleToggle}
        type="button"
      >
        <Icon name="download" classes={{ icon: styles.triggerIcon }} />
        {activeExportsCount > 0 && (
          <span className={styles.badge}>{activeExportsCount}</span>
        )}
      </button>

      {exportQueueOpen && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <span className={styles.title}>Eksportavimo eilė</span>
            <button
              className={styles.clearCompleted}
              disabled={!hasCompletedExports}
              onClick={handleClearCompleted}
              type="button"
            >
              Išvalyti atliktus
            </button>
          </div>

          <div className={styles.list}>
            {!visibleExports.length && !exportsLoading && (
              <div className={styles.emptyState}>Eksportų dar nėra.</div>
            )}

            {visibleExports.map(exportItem => {
              const fileSize = formatFileSize(exportItem);
              const progressPercent = getProgressPercent(exportItem);
              const exportDateTime = formatExportDateTime(exportItem);
              const canCancel = ACTIVE_EXPORT_STATUSES.includes(
                exportItem.status
              );
              const canDownload =
                exportItem.status === 'completed' &&
                Boolean(exportItem.file_url);

              return (
                <div className={styles.item} key={exportItem.id}>
                  <div className={styles.itemHeader}>
                    <div className={styles.itemMeta}>
                      <div className={styles.filename}>
                        {formatFileName(exportItem.filename)}
                      </div>
                      <div className={styles.fileDetails}>
                        {[
                          formatFileType(exportItem.filename),
                          exportDateTime,
                          fileSize,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>

                    <div className={styles.itemActions}>
                      <span
                        className={`${styles.status} ${
                          styles[`status_${exportItem.status}`]
                        }`}
                      >
                        <span className={styles.statusDot} />
                        {STATUS_LABELS[exportItem.status] || exportItem.status}
                      </span>

                      {canCancel && (
                        <button
                          className={styles.iconButton}
                          onClick={() => handleCancel(exportItem.id)}
                          type="button"
                        >
                          <Icon
                            name="close"
                            classes={{ icon: styles.actionIcon }}
                          />
                        </button>
                      )}

                      {canDownload && (
                        <button
                          className={`${styles.iconButton} ${styles.downloadButton}`}
                          onClick={() => handleDownload(exportItem)}
                          type="button"
                        >
                          <Icon
                            name="download"
                            classes={{ icon: styles.downloadActionIcon }}
                          />
                        </button>
                      )}
                    </div>
                  </div>

                  {exportItem.status === 'running' && (
                    <div className={styles.progressWrapper}>
                      <div className={styles.progressText}>
                        {exportItem.total_rows
                          ? `${formatRows(
                              exportItem.rows_exported
                            )} / ${formatRows(exportItem.total_rows)} eilučių`
                          : `${formatRows(exportItem.rows_exported)} eilučių`}
                      </div>

                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {exportItem.status === 'failed' && exportItem.error && (
                    <div className={styles.errorText}>{exportItem.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  exports: state.download.exports,
  dismissedExportIds: state.download.dismissedExportIds,
  exportQueueOpen: state.download.exportQueueOpen,
  exportsLoading: state.download.exportsLoading,
});

ExportQueue.propTypes = {
  dispatch: PropTypes.func.isRequired,
  exports: PropTypes.arrayOf(PropTypes.object).isRequired,
  dismissedExportIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  exportQueueOpen: PropTypes.bool.isRequired,
  exportsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(ExportQueue);
